import { Button, Group, Select, Stack, TextInput } from "@mantine/core";
import { useForm } from "@mantine/form";
import { useEffect } from "react";
import { Link } from "react-router-dom";
import { match, P } from "ts-pattern";
import { routes } from "@/configs/router.config/routes";
import useAuth from "@/hooks/useAuth";
import { useWorkspaces } from "@/hooks/useWorkspaces";
import { Role, roleDisplayName, User } from "@/models/user";

interface Props {
  submit: (email: string, name: string, role: Role, phoneNumber?: string) => Promise<void>;
  isLoading: boolean;
  isUpdate?: boolean;
  user?: User;
}

interface Form {
  email: string;
  name: string;
  role: Role;
  phoneNumber: string;
}

const UserEditForm: React.FC<Props> = (
  { submit, isLoading, isUpdate, user },
) => {
  const { authUser } = useAuth();
  const { sessionCurrentWorkspaceId } = useWorkspaces();

  const form = useForm<Form>({
    initialValues: {
      email: "",
      name: "",
      role: "user",
      phoneNumber: "",
    },
    validate: {
      email: (value) => {
        if (isUpdate) return null;
        return match(value)
          .with(
            P.when((v) => v.length === 0),
            () => "メールアドレスを入力してください。",
          )
          .with(
            P.when((v) => v.length > 254),
            () => "254文字以内で入力してください。",
          )
          .with(
            P.when((v) => !v.includes("@")),
            () => "メールアドレスが不正です",
          ).otherwise(() => null);
      },
      name: (value) =>
        match(value.length).with(0, () => "ユーザー名を入力してください。").with(
          P.when((v) => v > 30),
          () => "30文字以内で入力してください。",
        ).otherwise(() => null),
      phoneNumber: (value) => {
        if (!value) return null;
        return /^\+81\d{9,11}$/.test(value)
          ? null
          : "電話番号は+81から始まる日本の電話番号形式で入力してください（例: +819012345678）";
      },
    },
  });

  useEffect(() => {
    form.setValues({
      email: user?.email ?? "",
      name: user?.name ?? "",
      role: user?.role ?? "user",
      phoneNumber: user?.phoneNumber ?? "",
    });
  }, [user?.email, user?.name, user?.role, user?.phoneNumber]);

  // 自身の権限は変更不可
  const canEditRole = !isUpdate || (authUser && user) && authUser.id !== user.id;

  const handleClickSubmit = async () => {
    const result = form.validate();
    if (result.hasErrors) return;

    await submit(form.values.email, form.values.name, form.values.role, form.values.phoneNumber || undefined);
  };

  return (
    <form>
      <Stack gap={20}>
        <TextInput
          data-testid="email-input"
          label="メールアドレス"
          key={form.key("email")}
          w={300}
          required
          disabled={isUpdate}
          {...form.getInputProps("email")}
        />
        <TextInput
          data-testid="name-input"
          label="ユーザー名"
          key={form.key("name")}
          w={300}
          required
          {...form.getInputProps("name")}
        />
        <TextInput
          data-testid="phone-number-input"
          label="電話番号 (MFA用)"
          description="例: +819012345678"
          key={form.key("phoneNumber")}
          w={300}
          {...form.getInputProps("phoneNumber")}
        />
        <Select
          data-testid="role-select"
          label={canEditRole ? "権限" : "権限 (自身の権限は変更不可)"}
          disabled={!canEditRole}
          allowDeselect={false}
          w={200}
          maxDropdownHeight={200}
          style={{ width: "10rem" }}
          data={authUser?.selectableRoles().map((role) => ({ value: role, label: roleDisplayName(role) }))}
          {...form.getInputProps("role")}
        />
      </Stack>

      <Group>
        <Link to={routes.userList(sessionCurrentWorkspaceId)}>
          <Button
            mt="25px"
            variant="outline"
            color="gray"
          >
            キャンセル
          </Button>
        </Link>

        <Button
          data-testid="submit-button"
          disabled={isLoading}
          onClick={handleClickSubmit}
          mt="25px"
        >
          {isUpdate ? "更新する" : "作成する"}
        </Button>
      </Group>
    </form>
  );
};

export default UserEditForm;
